'use client'

import { useContext, useMemo } from 'react'
import useCurrentLocale from '../useCurrentLocale'
import { ConfigContext } from '../../../presentation/context/ConfigContext'
import Holidays from 'date-holidays'
import { StoreConfig } from '../../../domain/storeConfig'

const formatDateLocal = (local: string): string => {
  switch (local) {
    case 'us_en':
      return 'US'
    default:
      return 'FR'
  }
}

const calculateDeliveryDays = (isCarving: boolean, storeConfig: StoreConfig) => {
  const classicShiping = Number(storeConfig?.shipping_shipping_policy_delay)
  const carvingShiping =
    Number(storeConfig?.catalog_engraving_additional_shipping_delay) + classicShiping
  return isCarving ? carvingShiping : classicShiping
}

export const useCalculateDeliveryDays = (isCarving = false): number => {
  const { storeConfig } = useContext(ConfigContext)
  return calculateDeliveryDays(isCarving, storeConfig)
}

export const useCalculateDeliveryDate = (isCarving = false): Date => {
  const { storeConfig } = useContext(ConfigContext)
  const currentLocale = useCurrentLocale()
  const holidays = new Holidays()
  holidays.init(formatDateLocal(currentLocale))
  const allHollidays = holidays.getHolidays(new Date().getFullYear())

  const deliveryDate = useMemo((): Date => {
    const datesAvailableForShipping: Date[] = []

    const numberOfDaysForDelivery = calculateDeliveryDays(isCarving, storeConfig)

    let incrementedDate = new Date()

    while (datesAvailableForShipping.length < numberOfDaysForDelivery) {
      const deliveryDateIncremented = new Date(incrementedDate)
      deliveryDateIncremented.setDate(incrementedDate.getDate() + 1)

      const day = deliveryDateIncremented.getDate().toString().padStart(2, '0')
      const month = (deliveryDateIncremented.getMonth() + 1).toString().padStart(2, '0')
      const year = deliveryDateIncremented.getFullYear()

      const foundHoliday = allHollidays.find(
        (holiday) => holiday.date === `${year}-${month}-${day} 00:00:00`,
      )

      if (!foundHoliday && ![0, 6].includes(deliveryDateIncremented.getDay())) {
        datesAvailableForShipping.push(deliveryDateIncremented)
      }

      incrementedDate = deliveryDateIncremented
    }

    return datesAvailableForShipping[datesAvailableForShipping.length - 1]
  }, [
    storeConfig?.shipping_shipping_policy_delay,
    storeConfig?.catalog_engraving_additional_shipping_delay,
    currentLocale,
    isCarving,
  ])

  return deliveryDate
}
